import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, Button, TextArea } from '../../../core/components/ui';

/**
 * FAQ Form Modal Component
 *
 * Modal dialog for creating and editing FAQ entries.
 * Handles form state and validation.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Object|null} props.faq - FAQ object for editing (null for creating)
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onSubmit - Submit handler
 * @param {boolean} props.isSubmitting - Whether form is submitting
 *
 * @example
 * <FAQFormModal
 *   isOpen={formModalOpen}
 *   faq={faqToEdit}
 *   onClose={() => setFormModalOpen(false)}
 *   onSubmit={handleFormSubmit}
 *   isSubmitting={isCreating || isUpdating}
 * />
 */
export function FAQFormModal({ isOpen, faq, onClose, onSubmit, isSubmitting }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ question: '', answer: '' });

  // Update form data when faq prop changes
  useEffect(() => {
    if (faq) {
      setFormData({ question: faq.question, answer: faq.answer });
    } else {
      setFormData({ question: '', answer: '' });
    }
  }, [faq, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isValid = formData.question.trim() && formData.answer.trim();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={faq ? t('faq.editTitle', 'Edit FAQ') : t('faq.createTitle', 'Create New FAQ')}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Question Field */}
        <TextArea
          label={t('faq.question', 'Question')}
          value={formData.question}
          onChange={(value) => setFormData({ ...formData, question: value })}
          rows={3}
          required
          disabled={isSubmitting}
          id="faq-question"
        />

        {/* Answer Field */}
        <TextArea
          label={t('faq.answer', 'Answer')}
          value={formData.answer}
          onChange={(value) => setFormData({ ...formData, answer: value })}
          rows={6}
          required
          disabled={isSubmitting}
          id="faq-answer"
        />

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting
              ? t('common.saving', 'Saving...')
              : faq
              ? t('faq.update', 'Update')
              : t('faq.create', 'Create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

FAQFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  faq: PropTypes.shape({
    id: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    answer: PropTypes.string.isRequired,
  }),
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};
